import { computed, h, defineComponent, SetupContext, VNodeChildren } from 'vue';
// @ts-ignore
import { BField } from 'buefy/dist/esm/field';
import { ValidationError } from '@/entities/validation';
import { validationError } from '@/ui/dictionary';
import { StarredItem } from '@/ui/components/upgrade/StarredItem';
import { Tooltip } from '@/ui/components/Tooltip';
import { featureExplanationIcon } from '@/ui/components/icon/icons';
import { isNestedErrorForField } from '@/ui/helpers';

export interface FieldProps {
  name: string;
  label: string;
  required: boolean;
  errors?: ValidationError[];
  hideLabel?: boolean;
  horizontal?: boolean;
  starred?: boolean;
  classes?: string;
  disabled?: boolean;
  readonly?: boolean;
  tooltipText?: string;
  tooltipAppendToBody?: boolean;
  overrideErrorMessage?: string;
}

const getErrorMessageForField = (
  label: string,
  overrideErrorMessage: string | undefined,
  errors: ValidationError[],
) => {
  return overrideErrorMessage || errors.map(
    error => validationError(label, error),
  );
};

export const Field = defineComponent<FieldProps>({
  name: 'Field',
  props: [
    'name', 'label', 'required', 'errors', 'hideLabel', 'horizontal',
    'starred', 'classes', 'disabled', 'readonly', 'tooltipText', 'tooltipAppendToBody', 'overrideErrorMessage',
  ],
  setup(props, context: SetupContext) {
    const errorsForThisField = computed(() => props.errors?.filter(
      error => error.field === props.name || isNestedErrorForField(error.field, props.name),
    ) ?? []);
    const showLabel = props.hideLabel !== true;

    const labelWrap = (content: VNodeChildren) => [props.starred
      ? h(StarredItem, { props: { isStarred: props.starred } }, [content])
      : h('span', [content])];

    const tooltipWrap = (content: VNodeChildren) => props.tooltipText
      ? [content, h(Tooltip, {
        props: {
          label: props.tooltipText,
          position: 'is-bottom',
          multilined: true,
          appendToBody: props.tooltipAppendToBody || false,
          classes: 'tooltip__wrapper',
        },
      }, [
        featureExplanationIcon(),
      ])]
      : [content];

    const customClasses = [props.classes ?? ''];
    props.disabled && customClasses.push('label--disabled');
    props.readonly && customClasses.push('label--readonly');
    const customClass = customClasses.filter(c => !!c).join(' ');

    return function render() {
      return h(
        BField,
        {
          class: props.horizontal ? 'is-horizontal' : '',
          props: {
            type: { 'is-danger': errorsForThisField.value.length > 0 },
            message: errorsForThisField.value.length > 0
              ? getErrorMessageForField(props.label, props.overrideErrorMessage, errorsForThisField.value)
              : '',
            customClass,
          },
        },
        [
          showLabel ? h('template', { slot: 'label' }, tooltipWrap(labelWrap([
            props.label,
            // display required asterix
            props.required ? h('span', { class: 'has-text-danger' }, '*') : null,
          ]))) : null,
          context.slots.default?.(),
        ],
      );
    };
  },
});
